import { Component, inject, Input, OnChanges, OnInit, signal, SimpleChanges, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { CardsComponent } from '../../shared/cards/cards.component';
import { thisPeriodData, lastPeriodData } from '../../../../../shared/utils/dummyData';
import { BusinessIndustriesStatsComponent } from "../business-industries-stats/business-industries-stats.component";
import { DateRangePickerOutput, TimeUnit } from '../../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../../core/services/api.service';
import dayjs from 'dayjs';
import { sum } from 'd3';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";
import { UnitSuggestionService } from '../../../../../core/services/unit-suggestion.service';

@Component({
  standalone: true,
  selector: 'app-businesses-registered-stats',
  imports: [CardsComponent, BusinessIndustriesStatsComponent, IndividualToasterComponent],
  templateUrl: './businesses-registered-stats.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './businesses-registered-stats.component.scss'
})
export class BusinessesRegisteredStatsComponent implements OnInit, OnChanges {
  @Input() dateRange!: DateRangePickerOutput;
  @Input() productServerName: string = 'denefits';
  @Input() requiresAccessToken: boolean = false;
  @Input() cocuId: number = 1;
  @Input() countryId: number = 1;
  private unitService = inject(UnitSuggestionService);
  isLoading = signal(false)
  contracts = {
    hoveredType: undefined,
    loading: false,
    chart_value_type: 1,
    businessToday: {
      loading: false,
      total: 0,
      dental: 0,
      healthcare: 0,
      non_healthcare: 0
    },
    contractsData: [],
    previouscontractsData: [],
    stats: {},
    previousstats: {},
    current_data: {},
  };

  dummyStats = {
    total: 10,
    dental: 2,
    healthcare: 5,
    non_healthcare: 3
  }

  barData: any[] = [];
  // Data object supplied to the cards component for dummy rendering
  cardsData: any = [];
  private _datePickerParams: any = {
    start_time: new Date(),
    end_time: new Date(),
    previous_start_time: null,
    previous_end_time: null,
    unit: '',
    forceRun: false,
  };
  showIndustryWiseSegregation = signal(false);
  private viewSignal: WritableSignal<'chart' | 'table'> = signal('chart');

  constructor(
    private apiService: ApiService
  ) { }

  ngOnInit() {
    this.contracts.businessToday = { ...this.contracts.businessToday, ...this.dummyStats }
    this.FetchBusinessToday();

    // Initialize cardsData object
    this.cardsData = {
      chartData: [],
      previousChartData: [],
      tableData: [],
      previousTableData: [],
      summary: {
        current: 0,
        previous: undefined, // Start with undefined so hasPreviousData works correctly
        allTime: 0,
      },
      allTimeHeading: 'All Time Total Businesses'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detect changes to dateRange or productId input
    if ((changes['dateRange'] && changes['dateRange'].currentValue) || (changes['productServerName']) || (changes['cocuId']) || (changes['countryId'])) {
      if (this.dateRange?.previous_start_date && this.dateRange?.previous_end_date) {
        this.FetchComparisonData();
      } else {
        this.FetchTotalBusiness(false, true);
      }
    }
  }

  FetchBusinessToday(useCache?: boolean, useLoader = false, isPrevious = false) {
    this.fetchBusinessData(
      '/visionboard/stats/today_business',
      'businessToday',
      isPrevious,
      useCache,
      useLoader
    );
  }
  FetchTotalBusiness(useCache?: boolean, useLoader = false, isPrevious = false) {
    this.fetchBusinessData(
      '/visionboard/stats/total_business',
      'totalBusiness',
      isPrevious,
      useCache,
      useLoader
    );
  }

  private fetchBusinessData(
    apiKey: string,
    stateKey: string,
    isPrevious = false,
    useCache?: boolean,
    useLoader = false
  ) {
    this.isLoading.set(true)
    const {
      start_date,
      end_date,
      previous_start_date,
      previous_end_date,
      unit,
    } = this.dateRange;
    const payload: any = {
      start_time: isPrevious
        ? dayjs(previous_start_date).format('YYYY-MM-DD') || new Date()
        : dayjs(start_date).format('YYYY-MM-DD') || new Date(),
      end_time: isPrevious
        ? dayjs(previous_end_date).format('YYYY-MM-DD') || new Date()
        : dayjs(end_date).format('YYYY-MM-DD') || new Date(),
      unit: unit || 'day',
      is_dummy: 0,
      cocu_id: this.cocuId,
      country_id: this.countryId
    };

    if (stateKey === 'businessToday') {
      this.contracts.businessToday = {
        loading: true,
        total: 0,
        dental: 0,
        healthcare: 0,
        non_healthcare: 0
      };
    } else {
      if (isPrevious) {
        this.contracts.previouscontractsData = [];
        this.cardsData['previousChartData'] = [];
        this.cardsData['previousTableData'] = [];
      } else {
        this.contracts.contractsData = [];
        this.cardsData['chartData'] = [];
        this.cardsData['tableData'] = [];
      }
    }

    if (useLoader) {
      if (stateKey === 'businessToday')
        this.contracts.businessToday.loading = true;
      else this.contracts.loading = true;
    }

    // const payload = {
    //   start_time: '2025-07-29',
    //   end_time: '2025-10-29',
    //   unit: 'month',
    //   is_dummy: 0,
    //   hideLoader: 1,
    //   timezone: 'IST',
    //   access_token: '0fd57c6184472cf236eabe33b3770c47',
    //   admin_id: 207,
    //   outputFormat: 'db',
    // };

    // this.apiService.post('/visionboard/stats/total_business', payload, {
    //   addAccessToken: false,
    //   productServerName: 'denefits',
    //   showErrorToaster: true,
    //   showSuccessToaster: false,
    //   showLoader: true,
    // }).subscribe({
    //   next: (response: any) => {
   
    //   },
    //   error: (error: any) => {
    //     console.error('Denefits API Error:', error);
    //   },
    // });

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }

    this.apiService.post(apiKey, payload, {
        addAccessToken: this.requiresAccessToken,
        productServerName: this.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
.subscribe({
      next: (value: any) => {
        this.isLoading.set(false)
        const sumTotals = (arr: any[] = []) =>
          arr.reduce((s, i) => s + (i?.total ? +i.total : 0), 0);
        if (stateKey === 'businessToday') {
          this.contracts.businessToday = { ...value.data, loading: false };
        } else {
          const prefix = isPrevious ? 'previous' : '';
          this.contracts[`${prefix}contractsData`] = value.data.records || [];
          this.contracts[`${prefix}stats`] = value.data.stats || {};
          // this.contracts[`${prefix}tooltip`] = value.text || '';
          this.contracts.loading = false;
          if (!isPrevious) {
            this.cardsData['chartData'] =
              (value.data.records || []).map((item: any) => ({
                ...item,
                key: 'Active Businesses'
              }));
            this.cardsData['tableData'] = value.data.records || [];

            // Check if we have previous dates in dateRange
            const hasPreviousDates = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date;

            this.cardsData['summary'] = {
              current: value.data.stats['total_doctors'] || 0,
              allTime: value.data.stats['all_time_doctors_count'] || 0,
              // Only set previous to undefined if we don't have previous dates
              // If we do have previous dates, preserve existing previous value (will be updated by subsequent fetch)
              previous: hasPreviousDates ? this.cardsData['summary']?.previous : undefined
            };
            this.cardsData['allTimeHeading'] = 'All Time Total Businesses';

            // Clear previous data arrays if not in comparison mode
            if (!hasPreviousDates) {
              this.cardsData['previousChartData'] = [];
              this.cardsData['previousTableData'] = [];
            }
          } else {
            this.cardsData['previousChartData'] = (value.data.records || []).map((item: any) => ({
              ...item,
              key: 'Previous Businesses'
            }));
            this.cardsData['previousTableData'] = value.data.records || [];
            // Merge the previous stats with existing summary
            this.cardsData['summary'] = {
              ...this.cardsData['summary'],
              previous: value.data.stats['total_doctors'] || 0,
            };
          }
          this.createMainChart();
        }
      },
      error: (error: any) => {
        console.error('Denefits API Error:', error);
        this.contracts.businessToday.loading = false;
        this.isLoading.set(false)
      }
    });

    // this.Visionboard.statsAPI(apiKey, payload, useCache).subscribe(
    //   (res: any) => {
    //     if (res.is_error === 0 && res.data) {
    //       if (stateKey === 'businessToday') {
    //         this.contracts.businessToday = { ...res.data, loading: false };
    //       } else {
    //         const prefix = isPrevious ? 'previous' : '';
    //         this.contracts[`${prefix}contractsData`] = res.data.records || [];
    //         this.contracts[`${prefix}stats`] = res.data.stats || {};
    //         this.contracts[`${prefix}tooltip`] = res.text || '';
    //         this.contracts.loading = false;
    //         this.createMainChart();
    //       }
    //     }
    //   },
    //   () => {
    //     if (stateKey === 'businessToday')
    //       this.contracts.businessToday.loading = false;
    //     else this.contracts.loading = false;
    //   }
    // );
  }

  private createMainChart() {
    const valueKeys: { [key: number]: string; default: string } = {
      1: 'total_doctors',
      2: 'service_amount',
      3: 'downPayment_amount',
      4: 'financed_amount',
      default: 'total_contracts',
    };

    const key = valueKeys[this.contracts.chart_value_type] || valueKeys.default;
    const mapData = (arr: any[], labelSuffix = '') =>
      arr.map((d) => ({
        ...d,
        value: d[key],
        tooltip: `${d[key]} Businesses${labelSuffix}`,
        label: d.start,
        tick_start: d.start,
        tick_end: d.start,
      }));

    this.barData = [
      mapData(this.cardsData.chartData),
      mapData(this.cardsData.previousChartData, ' Previous Year'),
    ];
  }

  updateIndustryWiseSegregationView() {
    this.showIndustryWiseSegregation.set(!this.showIndustryWiseSegregation());
  }

  onToggle(event: any) {
    // ToggleBtnsComponent emits ViewMode: 'graph' | 'table'
    if (event === 'table' || event === 'graph') {
      this.viewSignal.set(event === 'table' ? 'table' : 'chart');
      return;
    }

    // Fallback for boolean or object payloads
    let value: boolean | undefined;
    if (typeof event === 'boolean') value = event;
    else if (event && typeof event === 'object') {
      value = (event.value ?? event.detail ?? event.payload ?? event.checked) as any;
      if (typeof value !== 'boolean') {
        if ('isTable' in event) value = !!event.isTable;
        else value = event === 'table' ? true : undefined;
      }
    }

    this.viewSignal.set(value ? 'table' : 'chart');
  }

  FetchComparisonData() {
    this.FetchTotalBusiness(false, true, false);
    this.FetchTotalBusiness(false, true, true);
  }

  private formatDateRangeByUnit(startDate: string, endDate: string, unit: string): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);



    switch (unit) {
      case 'hour':
        // For hourly: show date with hour range
        if (start.isSame(end, 'day')) {
          return `${start.format('DD MMM')} (${start.format('HH:mm')} - ${end.format('HH:mm')})`;
        }
        return `${start.format('DD MMM HH:mm')} - ${end.format('DD MMM HH:mm')}`;

      case 'day':
        // For daily: show date range
        if (start.isSame(end, 'month')) {
          return `${start.format('DD MMM YYYY')}`;
        }
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;

      case 'week':
        // For weekly: show date range
        return `${end.format('DD MMM YYYY')}`;

      case 'month':
        // For monthly: show month range
        if (start.isSame(end, 'year')) {
          return `${start.format('MMM YYYY')}`;
        }
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;

      case 'quarter':
        // For quarterly: show quarter range
        return `Q${start.quarter()} ${start.format('YYYY')} - Q${end.quarter()} ${end.format('YYYY')}`;

      case 'year':
        // For yearly: show year range
        if (start.year() === end.year()) {
          return start.format('YYYY');
        }
        return `${start.format('YYYY')} - ${end.format('YYYY')}`;

      default:
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
    }
  }

}
